// What a settings field says to a person, beside the generated descriptors (fields.gen.ts). The
// generated `label` and `description` come from the schema and are written for engineers: field
// keys in backticks, raw enum values, "Mfa". This map gives the screens plain words, keyed by the
// field's dotted path, without touching the schema contract or the generated file. A field the
// map does not name falls back to its descriptor, with any backticks dropped.
import type { Json } from "./inherit";
import type { FieldDescriptor } from "./validate";

const HARNESSES = ["claude_code", "codex", "other", "web"] as const;
const each = <T>(suffix: string, value: T): Record<string, T> => Object.fromEntries(HARNESSES.map((h) => [`models.${h}.${suffix}`, value]));

/** Labels the generated title-casing gets wrong. */
const LABELS: Readonly<Record<string, string>> = {
  "sign_in.iam_keys.require_mfa": "Require MFA",
  "sign_in.cognito.user_pool_id": "User pool ID",
  "sign_in.cognito.client_id": "App client ID",
  "sign_in.cognito.mfa": "MFA",
  "sign_in.key_policy.lifetime_days": "Key lifetime (days)",
  "sign_in.key_policy.idle_days": "Idle limit (days)",
  "sign_in.key_policy.max_per_person": "Keys per person",
  "admin_access.match": "Matchers",
};

/** The words for each enum value, by field path. */
const OPTIONS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  "sign_in.served_by": { central: "Centrally by Nexus", proxy: "The proxy itself" },
  "sign_in.cognito.mode": { disabled: "Off", create: "Create a new user pool", existing: "Use an existing user pool" },
  "sign_in.cognito.mfa": { optional: "Optional", required: "Required", off: "Off" },
  "identity.onboarding_mode": { open: "Anyone who signs in", approval: "Only after an admin approves" },
  ...each("mode", { follow_aws: "Every model AWS offers", allow_list: "Only the models listed" }),
  "guardrails.mode": { create: "Create a guardrail", existing: "Use an existing guardrail" },
  "guardrails.trace": { disabled: "Off", enabled: "On", enabled_full: "On, with the matched text" },
  "guardrails.stream_processing": { sync: "Check each chunk before it is sent", async: "Send text, block on a later check" },
  "quotas.enforcement": { on: "On", shadow: "Shadow (record only)", off: "Off" },
  "web_search.provider": { tavily: "Tavily", perplexity: "Perplexity", exa_ai: "Exa AI", serper: "Serper", brave: "Brave", agentcore: "AgentCore", null: "None" },
};

/** Plain-word hints; "" means the field needs none. */
const HINTS: Readonly<Record<string, string>> = {
  "sign_in.served_by": "Whether the proxy serves its own sign-in page or Nexus serves it centrally. AllCode changes this.",
  "sign_in.cognito.mode": "Off, a new user pool made when the proxy is deployed, or a user pool you already have.",
  "sign_in.cognito.user_pool_id": "The existing user pool, for example eu-west-1_AbCdEf123.",
  "sign_in.cognito.client_id": "The pool's app client the proxy signs in through.",
  "sign_in.cognito.mfa": "Whether the user pool asks for a second factor.",
  "sign_in.aws_sign_in.allowed_accounts": "AWS accounts, besides the proxy's own, whose people may sign in.",
  "sign_in.aws_sign_in.allowed_principals": "When the list has entries, only these roles, permission sets or users may sign in.",
  "sign_in.key_policy.lifetime_days": "How long a key lasts. 0 means it never expires.",
  "sign_in.key_policy.idle_days": "A key unused for this long stops working. 0 means never.",
  "sign_in.key_policy.max_per_person": "Keys one person may hold, one per device. Keys an admin issues don't count.",
  "admin_access.teams": "Members of these teams are admins.",
  "admin_access.match": "People who match one of these are admins, for example group:proxy-admins. A bare * is refused.",
  "admin_access.default_admins": "People who sign in with one of these email addresses are admins, before any team or matcher is set up. Case does not matter.",
  ...each("display_names", "The name a model picker shows for a model. Requests still go to the same model."),
  "guardrails.trace": "Whether Bedrock returns the guardrail's verdict with each response. The usage record never keeps the matched text.",
  "guardrails.stream_processing": "Whether streamed text waits for each check, or is sent and stopped on a later one.",
};

/** What one entry of a list must look like, for the add field's error. */
const ITEM_FORMATS: Readonly<Record<string, string>> = {
  "sign_in.aws_sign_in.allowed_accounts": "Use a 12-digit AWS account ID.",
  "sign_in.aws_sign_in.allowed_principals": "Use role/, permission-set/, user/ or federated-user/ and a name; * matches anything.",
  "admin_access.default_admins": "Use an email address.",
  "admin_access.match": "Start with group:, email:, attr:name=, role/, permission-set/, user/, federated-user/, account/ or iam-group/.",
  "identity.capture_attributes": "Letters, digits, and _ . : - only.",
};

/** What a person types into a list's add field, as a placeholder. */
const ITEM_EXAMPLES: Readonly<Record<string, string>> = {
  "sign_in.aws_sign_in.allowed_accounts": "123456789012",
  "sign_in.aws_sign_in.allowed_principals": "permission-set/AdminAccess",
  "admin_access.default_admins": "name@example.com",
  "admin_access.teams": "platform-team",
  "admin_access.match": "group:proxy-admins",
};

function plain(text: string): string {
  return text.replace(/`([^`]*)`/g, "$1");
}

export function fieldLabel(field: FieldDescriptor): string {
  return LABELS[field.path] ?? field.label;
}

export function fieldHint(field: FieldDescriptor): string {
  return HINTS[field.path] ?? plain(field.description);
}

export function optionLabel(path: string, value: Json): string {
  return OPTIONS[path]?.[String(value)] ?? String(value);
}

export function itemFormat(path: string): string | undefined {
  return ITEM_FORMATS[path];
}

export function itemExample(path: string): string | undefined {
  return ITEM_EXAMPLES[path];
}
